import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { ArrowUp, Loader2, Mic } from 'lucide-react'
import { type ComponentProps, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CoachAvatar } from '@/components/coach-avatar'
import { MarkdownContent } from '@/components/markdown-content'
import { useCoachDisplayName } from '@/hooks/use-coach-name'
import { useDictation } from '@/hooks/use-dictation'
import type { ChatMessage, useTaskExplain } from '@/hooks/use-task-explain'
import { useIsCoaching } from '@/lib/coaching-mode'
import { isEnterToSend } from '@/lib/enter-to-send'

// Shared "Ask <coach>" chat for the detail side sheets (goal, milestone, target,
// task, inbox task): coach header, wrapping chips that send straight away, a
// plain input, and the conversation rendered in the coach sheet's style.

export type AskChip = { label: string; full: string }
export type AskSubject = 'goal' | 'milestone' | 'target' | 'task'

export function useCoachPersona(enabled = true) {
  const prefs = useQuery(api.preferences.getMyPreferences, enabled ? {} : 'skip')
  const p = prefs as { coachVoice?: string; coachName?: string } | null | undefined
  const voice = p?.coachVoice
  const name = useCoachDisplayName(p)
  return { voice, name }
}

export function useAskChips(subject: AskSubject): AskChip[] {
  const { t } = useTranslation()
  if (subject === 'task')
    return [
      { label: t('goalSheet.howDo'), full: t('today.presetHowToFull') },
      { label: t('goalSheet.howStart'), full: t('today.presetWhereToStartFull') },
      { label: t('today.presetBreakItDownLabel'), full: t('today.presetBreakItDownFull') },
      { label: t('today.presetTipsLabel'), full: t('today.presetTipsFull') },
    ]
  return [
    { label: t('goalSheet.howStart'), full: t('today.presetWhereToStartFull') },
    { label: t('today.presetBreakItDownLabel'), full: t('today.presetBreakItDownFull') },
    { label: t('goalSheet.makeEasier'), full: t('goalSheet.makeEasierFull') },
    { label: t('today.presetTipsLabel'), full: t('today.presetTipsFull') },
  ]
}

type Chat = ReturnType<typeof useTaskExplain>

export function askChatActive(chat: Chat) {
  return chat.isLoading || !!chat.streamedText || !!chat.error || chat.history.length > 0
}

function Bubble({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-end">
      <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-foreground px-3 py-1.5">
        <p className="text-[13px] leading-snug whitespace-pre-wrap break-words text-background">
          {text}
        </p>
      </div>
    </div>
  )
}

function AskCoachThreadInner({
  chat,
  voice,
  chips,
}: {
  chat: Chat
  voice: string | undefined
  chips: AskChip[]
}) {
  const labelFor = new Map(chips.map((c) => [c.full, c.label]))
  const label = (text: string) => labelFor.get(text) ?? text
  const reply = (content: React.ReactNode) => (
    <div className="flex gap-2">
      <CoachAvatar voice={voice} size={22} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">{content}</div>
    </div>
  )
  if (!askChatActive(chat)) return null
  return (
    <div className="flex flex-col gap-4 border-t border-border/40 pt-4">
      {chat.history
        .reduce<ChatMessage[][]>((acc, msg) => {
          if (msg.role === 'user') acc.push([msg])
          else if (acc.length > 0) acc[acc.length - 1].push(msg)
          return acc
        }, [])
        .map((pair, i) => (
          <div key={i} className="flex flex-col gap-2">
            <Bubble text={label(pair[0].content)} />
            {pair[1] && reply(<MarkdownContent text={pair[1].content} />)}
          </div>
        ))}
      {chat.pendingQuestion && <Bubble text={label(chat.pendingQuestion)} />}
      {chat.isLoading && (
        <div className="flex items-center gap-2 py-1">
          <CoachAvatar voice={voice} size={22} />
          {[0, 150, 300].map((delay) => (
            <span
              key={delay}
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/40"
              style={{ animationDelay: `${delay}ms` }}
            />
          ))}
        </div>
      )}
      {chat.streamedText &&
        reply(<MarkdownContent text={chat.streamedText} isStreaming={chat.isStreaming} />)}
      {chat.error && <p className="text-xs text-destructive">{chat.error}</p>}
    </div>
  )
}

function AskCoachComposerInner({
  subject,
  chips,
  chat,
  coachName,
  onSend,
  resetKey,
}: {
  subject: AskSubject
  chips: AskChip[]
  chat: Chat
  coachName: string
  onSend: (text: string) => void
  // Clears the draft when the sheet switches to another item.
  resetKey?: string
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const ref = useRef<HTMLTextAreaElement>(null)
  const busy = chat.isLoading || chat.isStreaming
  const chatting = askChatActive(chat)
  const dictation = useDictation({ getValue: () => draft, onChange: setDraft })

  useEffect(() => setDraft(''), [resetKey])
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [draft])

  function send(text: string) {
    if (!text.trim() || busy) return
    onSend(text.trim())
    setDraft('')
  }

  return (
    <div className="flex flex-col gap-2.5 border-t border-border/60 bg-muted/20 px-3.5 pt-3 pb-3.5">
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={() => send(c.full)}
            disabled={busy}
            className="cursor-pointer rounded-full border border-border/70 bg-card px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary-text disabled:pointer-events-none disabled:opacity-50"
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="flex items-end gap-1 rounded-[26px] border border-border/70 bg-card p-1.5 transition-colors focus-within:border-border">
        {dictation.supported && (
          <button
            type="button"
            onClick={dictation.toggle}
            disabled={busy}
            aria-pressed={dictation.listening}
            aria-label={t(dictation.listening ? 'coach.stopDictation' : 'coach.dictate')}
            className={cn(
              'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors disabled:opacity-40',
              dictation.listening
                ? 'bg-destructive text-white'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <Mic className="size-[18px]" />
          </button>
        )}
        <textarea
          ref={ref}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (isEnterToSend(e)) {
              e.preventDefault()
              send(draft)
            }
          }}
          disabled={busy}
          rows={1}
          placeholder={
            chatting
              ? t('goalSheet.followUp')
              : `${t(`goalSheet.ask_${subject}`, { coach: coachName })}…`
          }
          className={cn(
            'min-h-9 max-h-[120px] flex-1 resize-none overflow-y-auto bg-transparent py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none disabled:opacity-50',
            !dictation.supported && 'pl-2.5',
          )}
        />
        <button
          type="button"
          onClick={() => send(draft)}
          disabled={busy || !draft.trim()}
          aria-label={t('common.send', { defaultValue: 'Send' })}
          className={cn(
            'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-default',
            draft.trim() && !busy
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {chat.isLoading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <ArrowUp className="size-4" strokeWidth={2.25} />
          )}
        </button>
      </div>
    </div>
  )
}

// The AI coach belongs to the personal side: hidden in coaching mode.
export function AskCoachThread(props: ComponentProps<typeof AskCoachThreadInner>) {
  return useIsCoaching() ? null : <AskCoachThreadInner {...props} />
}

export function AskCoachComposer(props: ComponentProps<typeof AskCoachComposerInner>) {
  return useIsCoaching() ? null : <AskCoachComposerInner {...props} />
}
